import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GateWaterfall } from '../components/intelligence/GateWaterfall';
import BotDecisions from '../components/titan/BotDecisions';
import { WarRoomDashboard } from '../components/intelligence/WarRoomDashboard';
import WarRoomAudit from './WarRoomAudit';
import { BrainCircuit, Filter, Target, Activity, ShieldCheck, Server, Cpu, Bot, CheckCircle2, AlertTriangle, ClipboardList, BarChart2 } from 'lucide-react';
import { titanApi, intelligenceApi } from '../lib/api';
import { useEquitiesMode } from '../hooks/useEquitiesMode';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const statusTone = (status?: string) => {
  const value = (status || '').toLowerCase();
  if (value.includes('ok') || value.includes('healthy') || value.includes('ready')) {
    return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/40';
  }
  if (value.includes('warn') || value.includes('degraded') || value.includes('attention')) {
    return 'text-amber-300 bg-amber-500/10 border-amber-500/40';
  }
  return 'text-rose-300 bg-rose-500/10 border-rose-500/40';
};

const SIGNAL_TABS = ['war-room', 'gates', 'audit'] as const;

export const IntelligenceHub: React.FC = () => {
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'war-room';
  const activeTab = SIGNAL_TABS.includes(tabParam as (typeof SIGNAL_TABS)[number])
    ? tabParam
    : 'war-room';
  const [dataHealth, setDataHealth] = useState(null);
  const [regime, setRegime] = useState(null);
  const [circuitBreaker, setCircuitBreaker] = useState(null);
  const [universe] = useState(null);
  const [masterControl, setMasterControl] = useState<any>(null);
  const [lifecycleRunning, setLifecycleRunning] = useState(false);

  useEffect(() => {
    const fetchGates = async () => {
      try {
        const [healthRes, regimeRes, breakerRes] = await Promise.allSettled([
          titanApi.health(),
          titanApi.marketRegime(),
          titanApi.circuitBreaker(),
        ]);
        if (healthRes.status === 'fulfilled') setDataHealth(healthRes.value as any);
        if (regimeRes.status === 'fulfilled') setRegime(regimeRes.value as any);
        if (breakerRes.status === 'fulfilled') setCircuitBreaker(breakerRes.value as any);
      } catch { /* non-fatal */ }
    };
    fetchGates();
    const interval = setInterval(fetchGates, 15000);
    return () => clearInterval(interval);
  }, []);

  const runLifecycleMaintenance = async () => {
    try {
      setLifecycleRunning(true);
      await intelligenceApi.runCatalystLifecycle();
      const refreshed = await intelligenceApi.masterControl();
      setMasterControl(refreshed as any);
    } catch {
      // non-fatal
    } finally {
      setLifecycleRunning(false);
    }
  };

  useEffect(() => {
    const fetchMasterControl = async () => {
      try {
        const res = await intelligenceApi.masterControl();
        setMasterControl(res as any);
      } catch {
        // non-fatal
      }
    };
    fetchMasterControl();
    const interval = setInterval(fetchMasterControl, 15000);
    return () => clearInterval(interval);
  }, []);

  const checks = (masterControl?.checks ?? {}) as Record<string, boolean>;
  const readyCount = Object.values(checks).filter(Boolean).length;
  const totalChecks = Object.keys(checks).length;
  const health = masterControl?.health ?? {};
  const watchdog = (masterControl?.data?.health_watchdog ?? {}) as {
    data_feed_fresh?: boolean;
    consecutive_stale_count?: number;
  };
  const dataPipeline = (masterControl?.data ?? {}) as {
    eodhd_ws_connected?: boolean;
    alpaca_data_streaming?: boolean;
    alpaca_ws_subscription?: {
      truncated_to_max?: boolean;
      output_count?: number;
      input_count?: number;
      max_subscriptions?: number;
    };
  };
  const dataDegradeReasons: string[] = [];
  if (watchdog.data_feed_fresh === false) {
    dataDegradeReasons.push('watchdog reports stale feed');
  }
  if ((watchdog.consecutive_stale_count ?? 0) > 0) {
    dataDegradeReasons.push(`stale cycles: ${watchdog.consecutive_stale_count}`);
  }
  if (!dataPipeline.eodhd_ws_connected && !dataPipeline.alpaca_data_streaming) {
    dataDegradeReasons.push('market websocket disconnected');
  }
  const wsSub = dataPipeline.alpaca_ws_subscription;
  if (wsSub?.truncated_to_max) {
    dataDegradeReasons.push(
      `WS cap: ${wsSub.output_count ?? '?'}/${wsSub.input_count ?? '?'} symbols (max ${wsSub.max_subscriptions ?? '?'})`,
    );
  }
  const dataHealthReason =
    String(health?.data_status || '').toLowerCase() === 'degraded' && dataDegradeReasons.length > 0
      ? dataDegradeReasons.join(' · ')
      : 'Market data + watchdog quality';
  const funnel = masterControl?.catalyst_autonomy?.funnel ?? {};
  const lifecycle = masterControl?.catalyst_autonomy?.lifecycle ?? {};
  const execReady = masterControl?.execution_readiness ?? {} as {
    can_execute_orders?: boolean;
    controller_mode?: string | null;
    blocking_reasons?: string[];
  };
  const pendingBlockers = (funnel?.pending_deploy_blockers ?? {}) as Record<string, number>;

  // Master-control "asset sessions" payload (backend: /master-control/telemetry)
  const assetSessions = (masterControl?.data?.asset_sessions ?? {}) as {
    crypto?: any;
    forex?: any;
  };
  const cryptoSession = assetSessions?.crypto ?? {};
  const forexSession = assetSessions?.forex ?? {};

  const freshnessPillClass = (fresh?: boolean, consecutiveStale?: number) => {
    const cs = consecutiveStale ?? 0;
    if (fresh === true && cs === 0) {
      return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
    }
    if (fresh === false || cs > 0) {
      return 'border-amber-500/40 bg-amber-500/10 text-amber-300';
    }
    return 'border-muted-foreground/30 bg-muted/30 text-muted-foreground';
  };

  return (
    <div className="flex-1 overflow-auto bg-muted/20 pb-10">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BrainCircuit className="w-8 h-8 text-primary" />
              Signals &amp; War Room
            </h1>
            <p className="text-muted-foreground mt-1">
              Catalyst pipeline, execution gates, and fleet audit — performance analytics live on{' '}
              <Link to="/reports" className="text-primary underline-offset-4 hover:underline">
                Reports
              </Link>
              .
            </p>
          </div>
          <div className="flex items-center gap-2 bg-background p-1.5 rounded-md border shadow-sm">
            <Filter className="w-4 h-4 text-muted-foreground ml-2" />
            {!equitiesOnly && (
            <select className="bg-transparent border-none text-sm font-medium focus:ring-0">
              <option>All Asset Classes</option>
              <option>Stocks Only</option>
              <option>Crypto Only</option>
              <option>Forex Only</option>
            </select>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-lg border bg-background p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Setup Readiness</p>
              <ShieldCheck className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {totalChecks ? `${readyCount}/${totalChecks}` : '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {masterControl?.setup_ready ? 'Platform ready' : 'Needs attention'}
            </p>
          </div>
          <div className="rounded-lg border bg-background p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Trading</p>
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-bold mt-2">{masterControl?.trading?.positions_open ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Open positions · Today PnL ${masterControl?.trading?.today_realized_pnl ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-background p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Data</p>
              <Server className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {masterControl?.data?.health_watchdog_ok ? 'Healthy' : 'Check'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              News signals {masterControl?.data?.news?.signals_count ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-background p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">AI</p>
              <Cpu className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-bold mt-2">{masterControl?.ai?.usage?.today?.calls ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Calls today · Cost ${masterControl?.ai?.usage?.today?.cost_usd ?? 0}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <div className={`rounded-lg border p-4 ${statusTone(health?.overall_status)}`}>
            <p className="text-xs uppercase tracking-wide">Overall Health</p>
            <p className="text-2xl font-bold mt-2">{health?.overall_status ?? 'unknown'}</p>
            <p className="text-xs mt-1">Master control health state</p>
          </div>
          <div className={`rounded-lg border p-4 ${statusTone(health?.trading_status)}`}>
            <p className="text-xs uppercase tracking-wide">Trading Health</p>
            <p className="text-2xl font-bold mt-2">{health?.trading_status ?? 'unknown'}</p>
            <p className="text-xs mt-1">Broker/controller/trading readiness</p>
          </div>
          <div className={`rounded-lg border p-4 ${statusTone(health?.data_status)}`}>
            <p className="text-xs uppercase tracking-wide">Data Health</p>
            <p className="text-2xl font-bold mt-2">{health?.data_status ?? 'unknown'}</p>
            <p className="text-xs mt-1">{dataHealthReason}</p>
          </div>
          <div className={`rounded-lg border p-4 ${statusTone(health?.ai_status)}`}>
            <p className="text-xs uppercase tracking-wide">AI Health</p>
            <p className="text-2xl font-bold mt-2">{health?.ai_status ?? 'unknown'}</p>
            <p className="text-xs mt-1">Routing, key, and usage checks</p>
          </div>
        </div>

        {masterControl?.issues?.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 mb-6">
            <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-2">
              <AlertTriangle className="w-4 h-4" />
              Master Control Alerts
            </div>
            <ul className="space-y-1 text-sm text-amber-100">
              {masterControl.issues.map((issue: string) => (
                <li key={issue} className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {execReady && Object.keys(execReady).length > 0 && (
          <div className="rounded-lg border bg-background p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-primary" />
              <p className="text-sm font-semibold">Order execution readiness</p>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Data can be healthy while the controller is in <span className="font-mono">scan-only</span> (no broker) — orders only run in <span className="font-mono">full</span> mode with a connected broker.
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`rounded border px-2 py-1 ${execReady.can_execute_orders ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-amber-500/40 bg-amber-500/10'}`}>
                Can execute orders: {execReady.can_execute_orders ? 'Yes' : 'No'}
              </span>
              <span className="rounded border px-2 py-1 bg-muted/30">
                Controller mode: <span className="font-mono">{execReady.controller_mode ?? '—'}</span>
              </span>
            </div>
            {(execReady.blocking_reasons?.length ?? 0) > 0 && (
              <ul className="mt-2 text-xs text-amber-200 list-disc pl-4 space-y-0.5">
                {(execReady.blocking_reasons ?? []).map((r: string) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {totalChecks > 0 && (
          <div className="rounded-lg border bg-background p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <p className="text-sm font-semibold">Master Control Checklist</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(checks).map(([key, ok]) => (
                <div key={key} className={`text-xs rounded px-2 py-1 border ${ok ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-rose-500/40 text-rose-300 bg-rose-500/10'}`}>
                  {key.replaceAll('_', ' ')}: {ok ? 'OK' : 'Needs Setup'}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-background p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">Catalyst Autonomy Funnel</p>
            <span className="text-xs text-muted-foreground">Detect → Generate → Deploy</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Detected</div>
              <div className="font-semibold text-base">{funnel?.detected ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Generated</div>
              <div className="font-semibold text-base">{funnel?.generated ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Auto Deployed</div>
              <div className="font-semibold text-base">{funnel?.auto_deployed ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Manual Queue</div>
              <div className="font-semibold text-base">{funnel?.manual_review ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Rejected</div>
              <div className="font-semibold text-base">{funnel?.rejected ?? 0}</div>
            </div>
          </div>
          <div className="mt-3 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Rejected / expired — reasons</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries((funnel?.rejection_reasons ?? {}) as Record<string, number>).length === 0 ? (
                  <span className="text-xs text-muted-foreground">
                    {(funnel?.rejected ?? 0) > 0
                      ? 'Counts include rejects without a stored reason — use API reject with a note, or check automation logs.'
                      : 'None'}
                  </span>
                ) : (
                  Object.entries((funnel?.rejection_reasons ?? {}) as Record<string, number>).map(([reason, count]) => (
                    <span key={reason} className="text-[11px] rounded border px-2 py-1 bg-muted/30">
                      {reason}: {count}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Manual queue — policy / infra blockers (sample)</p>
              <div className="flex flex-wrap gap-1">
                {Object.keys(pendingBlockers).length === 0 ? (
                  <span className="text-xs text-muted-foreground">No stored blocker text on pending rows yet</span>
                ) : (
                  Object.entries(pendingBlockers).map(([reason, count]) => (
                    <span key={reason} className="text-[11px] rounded border px-2 py-1 bg-muted/30">
                      {reason}: {count}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-background p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">Catalyst Lifecycle Automation</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Auto-expire + deactivation controls</span>
              <button
                type="button"
                onClick={runLifecycleMaintenance}
                disabled={lifecycleRunning}
                className="text-xs rounded border px-2 py-1 bg-muted/30 hover:bg-muted/50 disabled:opacity-60"
              >
                {lifecycleRunning ? 'Running…' : 'Run now'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Enabled</div>
              <div className="font-semibold text-base">{lifecycle?.enabled ? 'Yes' : 'No'}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Max Bot Age (hrs)</div>
              <div className="font-semibold text-base">{lifecycle?.max_bot_age_hours ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Retired Pending</div>
              <div className="font-semibold text-base">{lifecycle?.retired_pending_count ?? 0}</div>
            </div>
            <div className="rounded border px-2 py-2">
              <div className="text-muted-foreground">Deactivated Active</div>
              <div className="font-semibold text-base">{lifecycle?.deactivated_pattern_count ?? 0}</div>
            </div>
          </div>
          {lifecycle?.last_run_at && (
            <p className="text-xs text-muted-foreground mt-3">
              Last lifecycle run: {lifecycle.last_run_at}
            </p>
          )}
          {lifecycle?.reasons && Object.keys(lifecycle.reasons).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-1">Lifecycle reasons</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(lifecycle.reasons as Record<string, number>).map(([reason, count]) => (
                  <span key={reason} className="text-[11px] rounded border px-2 py-1 bg-muted/30">
                    {reason}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setSearchParams({ tab: v })}
          className="w-full"
        >
          <TabsList className="mb-6">
            <TabsTrigger value="war-room" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              War Room
            </TabsTrigger>
            <TabsTrigger value="gates" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Execution Gates
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Fleet Audit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="war-room" className="space-y-6">
            <WarRoomDashboard />
          </TabsContent>

          <TabsContent value="gates">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-6">
              <div className="xl:col-span-8">
              <GateWaterfall
                dataHealth={dataHealth}
                regime={regime}
                circuitBreaker={circuitBreaker}
                universe={universe}
              />
              </div>

              <div className="xl:col-span-4 rounded-lg border bg-background p-4 flex flex-col justify-center gap-3">
                <BarChart2 className="w-8 h-8 text-primary" />
                <p className="text-sm font-semibold">Performance &amp; quality</p>
                <p className="text-xs text-muted-foreground">
                  Expectancy and trade autopsy live on Reports.
                </p>
                <Link to="/reports" className="text-sm text-primary font-medium underline-offset-4 hover:underline">
                  Open Reports →
                </Link>
              </div>
            </div>

            {/* Asset-class readiness: Crypto + Forex (hidden in stocks-only mode) */}
            {!equitiesOnly && (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border bg-background p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Crypto Session</p>
                    <span className={`text-xs rounded border px-2 py-1 ${freshnessPillClass(cryptoSession?.fresh, cryptoSession?.consecutive_stale)}`}>
                      {cryptoSession?.fresh === true ? 'Fresh' : 'Degraded'}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Session</span>
                      <span className="text-foreground font-mono">{cryptoSession?.session ?? 'open'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Coinbase configured</span>
                      <span className="text-foreground">{cryptoSession?.coinbase_configured ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quote age</span>
                      <span className="text-foreground">{cryptoSession?.age_seconds != null ? `${cryptoSession.age_seconds}s` : '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stale cycles</span>
                      <span className="text-foreground">{cryptoSession?.consecutive_stale ?? 0}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border bg-background p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Forex Session (OANDA 24/5)</p>
                    <span className={`text-xs rounded border px-2 py-1 ${freshnessPillClass(forexSession?.fresh, forexSession?.consecutive_stale)}`}>
                      {forexSession?.fresh === true ? 'Fresh' : 'Degraded'}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Session</span>
                      <span className="text-foreground font-mono">
                        {forexSession?.session_open ? 'Open' : 'Closed'} ({forexSession?.session ?? 'unknown'})
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>OANDA configured</span>
                      <span className="text-foreground">{forexSession?.oanda_configured ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quote age</span>
                      <span className="text-foreground">{forexSession?.age_seconds != null ? `${forexSession.age_seconds}s` : '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stale cycles</span>
                      <span className="text-foreground">{forexSession?.consecutive_stale ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Scan gate threshold</span>
                      <span className="text-foreground">{forexSession?.threshold_seconds != null ? `${forexSession.threshold_seconds}s` : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            <div className="mt-8">
              <h2 className="text-xl font-bold mb-4">Signal Forensics Feed</h2>
              {/* Unified Decision Feed via existing BotDecisions component */}
              <BotDecisions />
            </div>
          </TabsContent>

          <TabsContent value="audit" className="space-y-4">
            <WarRoomAudit />
          </TabsContent>
        </Tabs>

      </div>
    </div>
  );
};

export default IntelligenceHub;
